import { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import OfferPageGallery from '../OfferPageGallery'
import Pane from 'components/Common/Pane'
import PackageOptions from './FlashTourPackageOptions'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import PaneBody from 'components/Common/Pane/PaneBody'
import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferIntro from './FlashTourIntro'
import OfferBNPLAd from '../OfferBNPLAd'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import { SummaryColumn } from '../styles'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import FlashTourSummary from './FlashTourSummary'
import { TourBookmarkButton } from 'wishlists/components/Bookmark/Buttons'
import WishlistGuard from 'wishlists/components/WishlistGuard'
import config from 'constants/config'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import FlashTourSocialProofDialog from './SocialProofDialog'
import UltraLuxUSPs from '../UltraLux/UltraLuxUSPs'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import { useOfferPackagePrice } from 'hooks/Offers/useOfferPrice'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import { getCanAgentExportMedia } from 'agentHub/selectors/agentHubSelectors'
import useShareVariant from 'hooks/useShareVariant'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const Summary = styled(Group)`
  word-break: break-word;
`

const RelativeBlock = styled.div`
  position: relative;
`

const ContentsCSSBreakpoint = styled(CSSBreakpoint)`
  display: contents;
`

interface Props {
  customerPortalURL: string
  memberId: string
  offer: App.TourOffer
  pathname: string
  regionCode: string
  defaultDepositAmountPercentage: number
  routeHistory: App.RouteHistoryState
}

function FlashTourContent(props: Props) {
  const {
    customerPortalURL,
    memberId,
    offer,
    pathname,
    regionCode,
    defaultDepositAmountPercentage,
    routeHistory,
  } = props

  const {
    id,
    images,
    locationHeading,
    locationSubheading,
    onlinePurchaseSchedule,
    packages,
  } = offer

  const intl = useIntl()
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)

  const scrollToPackageOptions = useCallback(() => {
    packageOptionsAnchor.current?.scrollIntoView()
  }, [])

  const availableForPurchase = scheduleIsCurrent(onlinePurchaseSchedule)
  const location = [locationHeading, locationSubheading].filter(t => t).join(', ')
  const shareVariant = useShareVariant()

  const shareConfig = getShareModalConfig({
    customerPortalURL,
    image: images[0],
    memberId,
    offerId: id,
    offerLocation: location,
    pathname,
    region: regionCode,
    type: 'offer',
    utmCampaign: 'webshare',
    offerType: 'tour_v2',
    shareVariant,
    intl,
  })

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])
  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  const canBookMultipleRooms = config.MULTIPLE_ROOM_BOOKING_ENABLED

  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined
  const canAgentExportMedia = getCanAgentExportMedia

  const pricing = useOfferPackagePrice(offer.lowestPricePackage, offer)
  const bundledPrice = pricing.totalPrice
  const bundledValue = pricing.totalValue
  const hotelDiscountPercent = getValueOffPercent(pricing.totalValue, pricing.totalPrice)
  const bundledDiscountPercent = getValueOffPercent(bundledValue, bundledPrice)
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent
  const showDiscountPill = availableForPurchase && !offer.isDiscountPillHidden && highestDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const loggedOutSavedPercent = showDiscountPill ? highestDiscountPercent : undefined

  // Do not show the Expired recommendation when it comes from my escapes
  const prevPath = routeHistory.prevPath
  const shouldShowLere = !prevPath.includes('/my-escapes/order')

  const shouldShowToolTip = useScreenSizeUp('tablet')

  return (<ProductPaletteProvider product={offer}>
    <FlashTourSocialProofDialog offer={offer} />
    <div>
      <WishlistGuard>
      </WishlistGuard>
      {shouldShowLere && <ExpiredDealsLereRecommendations
        hideOffers={false}
      />}
      <OfferPageGallery offer={offer} shareModalConfig={shareConfig} autoPlayVideo={!!offer.isVideoVimeoHero} />
      <RelativeBlock>
        <Pane>
          <PaneBody size="lg-y">
            <LayoutContainer>
              <VerticalSpacer gap={12}>
                <Group direction="horizontal" horizontalAlign="end" verticalAlign="center">
                  <OfferPageLabelGroup offer={offer} showBadge />
                  <ContentsCSSBreakpoint min="tablet">
                    <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
                      <ShareModal shareConfig={shareConfig} openOnHash="#share">
                        <ShareWithFriendsButton showToolTip={shouldShowToolTip} />
                      </ShareModal>
                      {isExportMediaEnabled && canAgentExportMedia && <ExportMediaContentButton size="medium" target="_blank" href={mediaExportURL} />}
                      <WishlistGuard>
                        <TourBookmarkButton offer={offer} />
                      </WishlistGuard>
                    </Group>
                  </ContentsCSSBreakpoint>
                </Group>
                <Summary direction="horizontal" gap={16} wrap="wrap" tabletWrap="nowrap">
                  <OfferIntro
                    offer={offer}
                    shareConfig={shareConfig}
                    showPackageOptions={scrollToPackageOptions}
                  />
                  <SummaryColumn direction="vertical" gap={16}>
                    <FlashTourSummary
                      price={offer.lowestPricePackage.price}
                      offer={offer}
                      availableForPurchase={availableForPurchase}
                      showPackageOptions={scrollToPackageOptions}
                    />
                    <OfferBNPLAd prices={allPrices} />
                  </SummaryColumn>
                </Summary>
                {offer.productType === 'ultra_lux_tour' && <UltraLuxUSPs />}
              </VerticalSpacer>
            </LayoutContainer>
          </PaneBody>
        </Pane>
        <ScrollAnchor ref={packageOptionsAnchor} />
        <SoftOfferPageWalledGarden>
          {!canBookMultipleRooms && packages.length > 0 && (
            <PackageOptions
              offer={offer}
              availableForPurchase={availableForPurchase}
              packages={packages}
              defaultDepositAmountPercentage={defaultDepositAmountPercentage}
            />
          )}
          {canBookMultipleRooms && (
            intl.formatMessage({
              id: 'sIQ7OxFeR1yPNuu3PEai',
              defaultMessage: 'MULTIPLE ROOM BOOKING IS ENABLED',
            })
          )}
        </SoftOfferPageWalledGarden>
      </RelativeBlock>
    </div>
    <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
  </ProductPaletteProvider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    regionCode: state.geo.currentRegionCode,
    pathname: state.router.location.pathname,
    customerPortalURL: state.system.customerPortalURL,
    memberId: state.auth.account.memberId,
    defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(state),
    routeHistory: state.routeHistory,
  }
}

export default connect(mapStateToProps)(FlashTourContent)
